import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import { DataTable, Column, SCIcon, ProgressBar } from '../../Common/Imports';
import { ApiResponse, ConfigStatus_EXT, Status_EXT } from '../../Models/Classes';
import ConfigStatusNew from './ConfigStatus_New';
import ConfigStatusEdit from './ConfigStatus_Edit';
import { useToast } from '../../Helpers/ToastHelper';

interface ConfigStatusListProps {
    stakeholderId: number;
}

export const ConfigStatusList: React.FC<ConfigStatusListProps> = ({ stakeholderId }) => {
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<ConfigStatus_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [internalStatus, setInternalStatus] = useState<Status_EXT[] | null>(null);
    const [EditData, setEditData] = useState<ConfigStatus_EXT | undefined>();
    const [CreateNewData, setCreateNewData] = useState<boolean>(false);

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<ConfigStatus_EXT[]>>(`StakeholderConfigs/Status/GetList/${stakeholderId}`);
            if (resp.data.code == 0) {
                SetTableData(resp.data.data);
            }
            const resp1 = await AxiosHelper.get<ApiResponse<Status_EXT[]>>(`Status/GetList`);
            if (resp1.data.code == 0) {
                setInternalStatus(resp1.data.data);
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, showToast, stakeholderId, t]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const openEdit = (data: ConfigStatus_EXT) => {
        setEditData(data);
    }

    const editBody = (data: ConfigStatus_EXT) => {
        return <SCIcon Name="Edit" Classes="text-secondary" Pointer OnClick={() => openEdit(data)} />;
    }

    return (
        <>
            {
                CreateNewData &&
                <ConfigStatusNew IDStakeholder={stakeholderId} OnSave={() => { setCreateNewData(false); GetData() }} />
            }
            {
                EditData &&
                <ConfigStatusEdit IdTemplate={EditData.idConfigStatus} OnSave={() => { setEditData(undefined); GetData() }} />
            }
            <div className="col d-flex justify-content-end">
                <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => setCreateNewData(true)}></SCIcon>
            </div>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        value={TableData}
                        size="small"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        stripedRows>
                        <Column
                            field="customerStatus"
                            header={t("Stakeholder.Configs_Status.CustomerStatus")}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            field="allowSend"
                            header={t("Stakeholder.Configs_Status.InternalStatus")}
                            body={(e: ConfigStatus_EXT) => { return internalStatus?.find(m => m.idStatus == e.idStatus)?.value }}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            field="allowSend"
                            header={t("Stakeholder.Configs_Status.AllowMail")}
                            body={(e: ConfigStatus_EXT) => { return e.allowMail ? t("Common.Booleans.Yes") : t("Common.Booleans.No") }}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            field="allowSend"
                            header={t("Stakeholder.Configs_Status.AllowCall")}
                            body={(e: ConfigStatus_EXT) => { return e.allowCall ? t("Common.Booleans.Yes") : t("Common.Booleans.No") }}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            body={(e: ConfigStatus_EXT) => editBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </>
    );
};

export default ConfigStatusList;
